import React from 'react'
// @src imports
import { BottomNav, defaultItems } from 'src/chrome/BottomNav/BottomNav'
import CardHistoryManager from 'src/card_history/containers/CardHistoryManager'
import { AccountRoute } from 'src/dashboard/routes/AccountRoute'
import { DashboardRoute } from 'src/dashboard/routes/DashboardRoute'
import { DraftedOrdersRoute } from 'src/dashboard/routes/DraftedOrdersRoute'

import { InvitesRoute } from 'src/dashboard/routes/InvitesRoute'
import { NotificationsRoute } from 'src/dashboard/routes/NotificationsRoute'
import { PaymentSettingsRoute } from 'src/dashboard/routes/PaymentSettingsRoute'
import { PurchasePointsRoute } from 'src/dashboard/routes/PurchasePointsRoute'
import { CardHistoryRoute } from 'src/dashboard/routes/CardHistoryRoute'
import { DashboardHeader, DashboardPageLayout } from 'src/dashboard/components'

// relative imports
import DashboardHomeView from '../Views/Home/Home'
import Invites from '../Invites/Invites'
import Notifications from '../Notifications/Notifications'
import AnnouncementBanners from '../../../marketing/AnnouncementBanners/AnnouncementBanners'
import styles from './styles'
import { PricingRoute } from 'src/marketing/plans/routes/PricingRoute'
import { LeaderBoardRoute } from 'src/dashboard/routes/LeaderBoardRoute'
import { LeaderBoardList } from 'src/dashboard/components/Leaderboard/LeaderBoardList'
import { PricingPage } from 'src/pricing_page/PricingPage'
import { DraftsView } from '../Views/Drafts/DraftsView'
import { PlansAndPaymentView } from '../Views/PlansAndPayment/PlansAndPayment'
import { ShareSettingsView } from '../Views/Sharing/ShareSettingsView'
import { ShareSettingsRoute } from 'src/dashboard/routes/ShareSettingsRoute'
import { DashboardSettings } from '../Views/Settings/DashboardSettings'
import { DashboardSettingsRoute } from 'src/dashboard/routes/Settings'
import { Div, DropDownMenuProps, Flex } from '@sendoutcards/quantum-design-ui'
import { useEffect, useFlagsWithUseQueries, useState } from 'src/hooks'
import { DraftedCardsRoute } from 'src/dashboard/routes/DraftedCardsRoute'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { sponsorParam } from 'src/dashboard/navigationData'
import { AccountFragment } from 'src/graphql/generated/graphql'
import { MyGiftsRoute } from 'src/dashboard/routes/MyGiftsRoute'
import GiftCardWallet from 'src/dashboard/components/GiftCardWallet/GiftCardWallet'
import { Dialog } from 'src/design_system/molecules/dialog/Dialog'

export const dropDownMenuLinks = (
  account?: AccountFragment,
): DropDownMenuProps => {
  const sponsorLinkParam = account ? sponsorParam(account) : ''

  return {
    linkColumn: {
      title: '',
      links: [
        {
          title: (
            <Flex flexDirection="column">
              <Icon name="soc_logo" size={200} color="#000" />
              <Flex
                alignItems="center"
                style={{
                  fontFamily: 'Ubuntu',
                  fontStyle: 'italic',
                  fontSize: '14px',
                }}
                justifyContent="flex-end"
                flexDirection="row"
                columnGap="x_25"
              >
                The
                <Icon name="promptings_logo_full" size={90} color="#000" />
                Mechanism
              </Flex>
            </Flex>
          ),
          href: '/app',
          target: '_self',
        },
        {
          title: (
            <Flex flexDirection="column">
              <Icon name="promptings_logo_full" size={170} color="#000" />
              <Flex
                alignItems="center"
                style={{
                  fontFamily: 'Ubuntu',
                  fontStyle: 'italic',
                  fontSize: '14px',
                }}
                justifyContent="flex-end"
                flexDirection="row"
                columnGap="x_5"
              >
                Powered by
                <Icon name="soc_logo" size={120} color="#000" />
              </Flex>
            </Flex>
          ),
          onClick: () =>
            window.open(`https://www.promptings.com/${sponsorLinkParam}`),
          href: '',
          target: '_blank',
        },
      ],
    },
    top: 60,
    isOpen: false,
  }
}
type DashboardProps = {
  route: DashboardRoute
}
const routeViews = (route: DashboardRoute, hasGiftsWallet: boolean) => ({
  [AccountRoute.path]: <DashboardHomeView />,
  [DraftedOrdersRoute.path]: <DraftsView />,
  [DraftedCardsRoute.path]: <DraftsView />,
  [MyGiftsRoute.path]: hasGiftsWallet ? <GiftCardWallet /> : <></>,
  [InvitesRoute.path]: <Invites />,
  [NotificationsRoute.path]: <Notifications />,
  [CardHistoryRoute.path]: (
    <CardHistoryManager
      route={route.subroute as CardHistoryRoute}
      initialSection={(route.subroute as CardHistoryRoute).section}
      initialFilters={(route.subroute as CardHistoryRoute).filters}
    />
  ),
  [PaymentSettingsRoute.path]: <PlansAndPaymentView />,
  [ShareSettingsRoute.path]: <ShareSettingsView />,
  [DashboardSettingsRoute.path]: <DashboardSettings />,
  [PurchasePointsRoute.path]: <PricingPage />,
  [PricingRoute.path]: <PricingPage />,
  [LeaderBoardRoute.path]: <LeaderBoardList />,
})
type DashboardRouteViews = ReturnType<typeof routeViews>
export type DashboardRoutes = keyof DashboardRouteViews
const routeTitles: Partial<Record<DashboardRoutes, string>> = {
  [AccountRoute.path]: 'Home',
  [DraftedOrdersRoute.path]: 'My Drafts',
  [DraftedCardsRoute.path]: 'My Drafts',
  [MyGiftsRoute.path]: 'My Gift Cards',
  [InvitesRoute.path]: 'Invites',
  [NotificationsRoute.path]: 'Notifications',
  [CardHistoryRoute.path]: 'Card History',
  [PaymentSettingsRoute.path]: 'My Account',
  [ShareSettingsRoute.path]: 'Sharing',
  [DashboardSettingsRoute.path]: 'Settings',
  [PurchasePointsRoute.path]: 'Purchase Points',
  [PricingRoute.path]: 'Pricing',
  [LeaderBoardRoute.path]: 'Leaderboard',
}

const Dashboard: React.FC<DashboardProps> = props => {
  const { route } = props
  const routePath = route.subroute.path

  const { hasGiftCardWallet } = useFlagsWithUseQueries()
  const currentRouteView = routeViews(route, hasGiftCardWallet)[
    route.subroute.path
  ]
  const pageTitle = routeTitles[route.subroute.path]
  const shouldDisableLayoutPadding =
    routePath === PricingRoute.path || routePath === PurchasePointsRoute.path

  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  useEffect(() => {
    if (!shouldDisableLayoutPadding) return
    const scrollContainer = document.getElementById(
      'dashboard-scroll-container',
    )
    if (scrollContainer) {
      scrollContainer.scrollTop = 0
    }
  }, [routePath, shouldDisableLayoutPadding])

  // Don't render dashboard layout in editor
  if (
    route.subroute.path === '/card-history' &&
    (route.subroute?.subroute?.path === '/:cardId/resend' ||
      route.subroute?.subroute?.path === '/:cardId/edit')
  ) {
    return currentRouteView
  }

  return (
    <Div position="relative">
      <Div css={styles.dashboardWrapper}>
        <DashboardHeader
          toggleReminders={() => setIsNotificationsOpen(open => !open)}
          routePath={routePath}
        />
        <Div css={styles.dashboardContainer}>
          <Flex
            flexDirection={'column'}
            width="100%"
            style={{ height: '100%', minHeight: 0, flex: 1 }}
          >
            <DashboardPageLayout
              title={pageTitle}
              disableOuterPadding={shouldDisableLayoutPadding}
              disableContentPadding={shouldDisableLayoutPadding}
              outerHorizontalPaddingOverride={
                shouldDisableLayoutPadding ? 16 : undefined
              }
              outerBottomPaddingOverride={
                shouldDisableLayoutPadding ? 0 : undefined
              }
              titleTextMarginLeftOverride={
                shouldDisableLayoutPadding ? 26 : undefined
              }
            >
              <AnnouncementBanners />
              {currentRouteView}
            </DashboardPageLayout>
            <Dialog
              isOpen={isNotificationsOpen}
              onClose={() => setIsNotificationsOpen(false)}
              title="Reminders"
              maxWidth="1040px"
              padding="0"
            >
              <Div inset={{ horizontal: 'x2', vertical: 'x1' }}>
                <Notifications />
              </Div>
            </Dialog>
          </Flex>
        </Div>
        <div
          css={styles.bottomNavWrapper}
          style={{
            width: '100%',
            overflowY: 'scroll',
          }}
        >
          <div
            css={styles.bottomNav}
            style={{
              justifyContent: 'space-between',
              width: 'min-content',
              overflowY: 'scroll',
            }}
          >
            <BottomNav items={defaultItems} rootCSS={styles.rootNavWrapper} />
          </div>
        </div>
      </Div>
    </Div>
  )
}

export default Dashboard
